import React from 'react';
import { useAutoRollCountdown } from '@root/hooks';
import { appSelector, AppSliceState } from '@root/store/slices/appSlice';
import { balanceSelector } from '@root/store/slices/balanceSlice';
import { roundSelector } from '@root/store/slices/roundSlice';
import { useActions, useTypedSelector } from '@root/store/store';
import { useImageUrl } from '@shared/components/ImageContext';
import { useAnalytics, useBetLimits } from '@shared/hooks';
import { formatMoney } from '@shared/lib';
import { AnalyticsEvent } from '@shared/services/analytics';
import { useTranslation } from 'react-i18next';
import styles from './GameLimitsModal.module.scss';

// The browser knows currency names, so we don't keep a table.
function currencyName(code: string, locale: string): string {
  if (!code) return '';
  try {
    const name = new Intl.DisplayNames([locale], { type: 'currency' }).of(code);
    return name && name !== code ? `${code} · ${name}` : code;
  } catch {
    return code;
  }
}

const GameLimitsModal: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { gameLimitsModalOpen } = useTypedSelector<AppSliceState>(appSelector);
  const { balance } = useTypedSelector(balanceSelector);
  const { round } = useTypedSelector(roundSelector);
  const { setGameLimitsModalOpen } = useActions();
  const { trackEvent } = useAnalytics();
  const { minBet, maxBet } = useBetLimits();
  const { img, imgProps } = useImageUrl();
  const seconds = useAutoRollCountdown(
    round?.state?.rollDeadline,
    gameLimitsModalOpen
  );

  if (!gameLimitsModalOpen) return null;

  const symbol = balance.currencySymbol;
  const close = () => {
    trackEvent(AnalyticsEvent.INSTANT_GAME_LIMITS_CANCEL);
    setGameLimitsModalOpen(false);
  };

  const rows: [string, string][] = [
    [t('Minimum per paid roll'), formatMoney(symbol, minBet)],
    [t('Maximum per paid roll'), formatMoney(symbol, maxBet)],
  ];

  return (
    <div className={styles.scrim}>
      <div
        className={styles.dialog}
        style={
          { '--panel-skin': `url(${img('PopupFrame')})` } as React.CSSProperties
        }
      >
        <div className={styles.head}>
          <h2 className={styles.title}>{t('Game limits')}</h2>
          <button
            type="button"
            className={styles.close}
            aria-label={t('Close')}
            onClick={close}
          >
            <img {...imgProps('Icon_Close')} alt="" />
          </button>
        </div>

        <div className={styles.currency}>
          <span className={styles.currencyLabel}>{t('YOUR CURRENCY')}</span>
          <span className={styles.currencyValue}>
            {currencyName(balance.userCurrency, i18n.language)}
          </span>
        </div>

        {rows.map(([label, value]) => (
          <div className={styles.row} key={label}>
            <span className={styles.rowLabel}>{label}</span>
            <span className={styles.rowValue}>{value}</span>
          </div>
        ))}

        <p className={styles.note}>
          {t(
            'Limits apply to each paid roll. Your total round cost may be higher because a round can require several rolls.'
          )}
        </p>
        <p className={styles.note}>
          {t(
            'You need enough balance for the first paid roll to start. Free rolls cost {{zero}}; their rewards use your locked roll amount.',
            { zero: formatMoney(symbol, 0) }
          )}
        </p>

        {seconds > 0 && (
          <p className={styles.active}>
            {t('Active round · auto-roll in {{seconds}}s', { seconds })}
          </p>
        )}
      </div>
    </div>
  );
};

export default GameLimitsModal;
